import { convertDate, displayDateFormat } from '@/constants/dates';
import { useGetTestResults } from '@/queries/testing-plan.queries';
import { GridItem } from '@chakra-ui/react';
import { Box, Divider, Grid, Modal, Text } from '@cmpsr/components';
import React, { useEffect } from 'react';
import useAnalyticsCalls from '@/hooks/use-analytics';

type ModalTestResultsProps = {
  isOpen: boolean;
  onClose: () => void;
  idTestResults: string;
};

export default function ModalTestResults({ isOpen, onClose, idTestResults }: ModalTestResultsProps) {
  const { data: testResults, isFetched } = useGetTestResults(idTestResults);
  const { callTestResultsModalViewed } = useAnalyticsCalls();

  useEffect(() => {
    if (isFetched) callTestResultsModalViewed(testResults?.type ?? '', idTestResults);
  }, [testResults, isFetched]);

  if (!testResults) return '';

  const { type, date, body, header } = testResults;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <Modal.Overlay bg="background-overlay" backdropFilter="auto" />
      <Modal.Content
        bg="background-page"
        borderRadius={{ base: '0', lg: 'radii-2' }}
        border="1px solid"
        borderColor="ui-element-outline-default"
        w={{ base: 'full', lg: '80%' }}
        h={{ base: 'full', lg: 'auto' }}
        maxW="1184px"
        pl={{ base: 'spacer-5', lg: 'spacer-10' }}
        pb={{ base: 'spacer-5', lg: 'spacer-10' }}
        pt="spacer-4"
        pr="spacer-4"
        gap="spacer-6"
      >
        <Modal.Header
          bg="background-page"
          p={0}
          textAlign="left"
          flexDirection="row"
          display="flex"
          justifyContent="space-between"
        >
          <Box pt="spacer-6">
            <Text variant="text-header-S">TESTING RESULTS</Text>
            <Text textStyle="text-header-2xl-v">{type}</Text>
          </Box>
          <Modal.CloseButton borderRadius="50%" p="spacer-5" border="1px solid" borderColor="primary-default" />
        </Modal.Header>
        <Text variant="text-body-floating-label-medium" color="text-secondary">
          Date of screening: {convertDate(date, displayDateFormat)}
        </Text>
        <Divider />
        <Grid
          templateColumns="1fr 2fr"
          width="100%"
          borderRadius="radii-3"
          border="1px solid"
          borderColor="ui-element-divider"
          p="spacer-6"
          gap="spacer-3"
          textTransform="capitalize"
        >
          {Object.keys(header).map((key) => (
            <React.Fragment key={key}>
              <Text variant="text-body-regular" color="text-secondary">
                {key}
              </Text>
              <Text variant="text-body-medium">{header[key]}</Text>
            </React.Fragment>
          ))}
          {!!body && (
            <GridItem colSpan={2}>
              <Divider />
            </GridItem>
          )}
          {!!body &&
            Object.keys(body).map((key) => (
              <React.Fragment key={key}>
                <Text variant="text-body-meta-regular" color="text-secondary">
                  {key}
                </Text>
                <Text variant="text-body-meta-regular">{body[key]}</Text>
              </React.Fragment>
            ))}
        </Grid>
      </Modal.Content>
    </Modal>
  );
}
